/**
 * Firebase Authentication Service
 *
 * Provides authentication utilities for:
 * - User registration with Firebase
 * - User login with Firebase
 * - Password reset
 * - Email verification
 * - Token management
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  User as FirebaseUser,
  UserCredential,
} from 'firebase/auth';
import { auth } from '../config/firebase';

/**
 * Register a new user with Firebase Authentication
 */
export const registerWithFirebase = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);

    // Send email verification
    if (userCredential.user && !userCredential.user.emailVerified) {
      await sendEmailVerification(userCredential.user);
    }

    return userCredential;
  } catch (error: any) {
    throw new Error(getFirebaseErrorMessage(error.code));
  }
};

/**
 * Sign in with email and password
 */
export const loginWithFirebase = async (
  email: string,
  password: string
): Promise<UserCredential> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential;
  } catch (error: any) {
    throw new Error(getFirebaseErrorMessage(error.code));
  }
};

/**
 * Sign out the current user
 */
export const logoutFromFirebase = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: any) {
    throw new Error('Failed to sign out. Please try again.');
  }
};

/**
 * Send password reset email
 */
export const resetPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    throw new Error(getFirebaseErrorMessage(error.code));
  }
};

/**
 * Get Firebase ID token for the current user
 */
export const getFirebaseToken = async (): Promise<string | null> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    const token = await user.getIdToken();
    return token;
  } catch (error) {
    console.error('Error getting Firebase token:', error);
    return null;
  }
};

/**
 * Get current Firebase user
 */
export const getCurrentFirebaseUser = (): FirebaseUser | null => {
  return auth.currentUser;
};

/**
 * Resend email verification
 */
export const resendEmailVerification = async (): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user is currently signed in');
  }

  if (user.emailVerified) {
    throw new Error('Email is already verified');
  }

  try {
    await sendEmailVerification(user);
  } catch (error: any) {
    throw new Error('Failed to send verification email. Please try again.');
  }
};

/**
 * Convert Firebase error codes to user-friendly messages
 */
const getFirebaseErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'This email address is already registered. Please login or use a different email.';
    case 'auth/invalid-email':
      return 'Invalid email address. Please check and try again.';
    case 'auth/operation-not-allowed':
      return 'Email/password authentication is not enabled. Please contact support.';
    case 'auth/weak-password':
      return 'Password is too weak. Please use at least 6 characters.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/invalid-credential':
      return 'Invalid email or password. Please check your credentials.';
    case 'auth/too-many-requests':
      return 'Too many failed login attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your internet connection.';
    case 'auth/requires-recent-login':
      return 'This operation requires recent authentication. Please login again.';
    default:
      return `Authentication error: ${errorCode}. Please try again.`;
  }
};

export default {
  registerWithFirebase,
  loginWithFirebase,
  logoutFromFirebase,
  resetPassword,
  getFirebaseToken,
  getCurrentFirebaseUser,
  resendEmailVerification,
};
